import { templateIdForServer } from '@electron/shared/mcp-template-gating';
import { MACOS_CALENDAR_TEMPLATE_ID } from '@littlebird/apple-calendar/contract';
import { config, type Report, type Routine, useStores } from '@littlebird/sdk';
import { isRoutineDueOn } from '@littlebird/ui-utils';
import { isSameDay } from 'date-fns/isSameDay';
import { isToday } from 'date-fns/isToday';
import { useEffect } from 'react';

import {
  useUpcomingCalendarEvents,
  type UpcomingMeetingWithStatus,
} from '@/features/meetings/meetings.hooks';
import {
  routineReadState,
  useRoutineRefresh,
} from '@/features/routines/use-routine-refresh';
import { useVisibleRoutines } from '@/features/settings/proactive-agent.query';
import { useMcpServersQuery } from '@/features/settings/sub-settings/settings-local-mcp.query';
import { useEventKitState } from '@/integrations/useEventKitState';
import { useIntegrationState } from '@/integrations/useIntegrationState';
import { logError } from '@/lib/errorLogging';
import { NEW_CHAT_ID } from '@/utils/constants';

import { isHomeScheduleVisible, selectScheduleMeetings } from './schedule';

import type { ScheduleFilterMode } from './widgets.types';

type ScheduleMeetings = {
  meetings: UpcomingMeetingWithStatus[];
  totalCount: number;
  hasMore: boolean;
  todayCount: number;
  hasAnyEventToday: boolean;
  isLoading: boolean;
};

export const useScheduleMeetings = (options?: {
  excludeSoloEvents?: boolean;
  filterMode?: ScheduleFilterMode;
  todayOnly?: boolean;
  maxRows?: number;
}): ScheduleMeetings => {
  const { upcomingMeetingsWithStatus, isLoading } = useUpcomingCalendarEvents({
    excludeSoloEvents: options?.excludeSoloEvents,
  });
  return {
    ...selectScheduleMeetings(
      upcomingMeetingsWithStatus,
      options?.filterMode ?? 'all',
      options?.todayOnly,
      options?.maxRows,
      config.meetings.exclude_solo_events
    ),
    isLoading,
  };
};

export const useHomeScheduleVisible = (): boolean => {
  const { hasAnyEventToday, isLoading } = useScheduleMeetings({
    excludeSoloEvents: false,
    todayOnly: true,
  });
  const hasCalendar = useHasCalendarConnected();
  return isHomeScheduleVisible({ isLoading, hasCalendar, hasAnyEventToday });
};

export const useHasCalendarConnected = (): boolean => {
  const { integrationStates } = useIntegrationState();
  const { calendarGranted } = useEventKitState();
  const { data: mcpServers } = useMcpServersQuery();
  const hasAppleCalendarServer = (mcpServers ?? []).some(
    (server) =>
      server.enabled !== false &&
      templateIdForServer(server) === MACOS_CALENDAR_TEMPLATE_ID
  );
  return (
    (calendarGranted && hasAppleCalendarServer) ||
    integrationStates.some(
      (s) => s.isConnected && s.info.toolkit_name === 'googlecalendar'
    )
  );
};

export type RoutineState = 'ran' | 'pending' | 'idle';

type TodaysRoutineRow = {
  routine: Routine;
  state: RoutineState;
  todayReport?: Report;
};

const ROUTINES_MAX_ROWS = 4;

export const useTodaysRoutines = ({ maxRows = ROUTINES_MAX_ROWS } = {}) => {
  // Reads MobX state that changes in place, which React Compiler would memoize on the stable stores
  'use no memo';
  const { routines, reports } = useStores();
  useRoutineRefresh();

  const isLoading =
    !routineReadState.hasLoadedRoutines || !routineReadState.hasLoadedReports;
  const routineList = useVisibleRoutines(routines.userRoutines);
  const reportList = reports.orderedReports;

  const todayReportByTemplate = new Map<number, Report>();
  for (const report of reportList) {
    if (!isToday(report.scheduledAt)) continue;
    const existing = todayReportByTemplate.get(report.reportTemplateId);
    if (!existing) {
      todayReportByTemplate.set(report.reportTemplateId, report);
      continue;
    }
    if (report.createdAt.getTime() > existing.createdAt.getTime()) {
      todayReportByTemplate.set(report.reportTemplateId, report);
    }
  }

  const today = new Date();
  const rows: TodaysRoutineRow[] = [];
  for (const routine of routineList) {
    if (routine.isPaused) continue;
    const todayReport = todayReportByTemplate.get(routine.referenceId);

    if (todayReport) {
      rows.push({ routine, state: 'ran', todayReport });
    } else if (isRoutineDueOn(routine.recurrenceRule, today)) {
      rows.push({ routine, state: 'pending' });
    }
  }

  rows.sort((a, b) => {
    if (a.state !== b.state) {
      return a.state === 'ran' ? -1 : 1;
    }
    if (a.state === 'ran' && b.state === 'ran') {
      const aTs = a.todayReport ? a.todayReport.createdAt.getTime() : 0;
      const bTs = b.todayReport ? b.todayReport.createdAt.getTime() : 0;
      return bTs - aTs;
    }
    return a.routine.title.localeCompare(b.routine.title);
  });

  return {
    rows: rows.slice(0, maxRows),
    hasAnyRoutine: routineList.length > 0,
    isLoading,
  };
};

/** The meetings last recorded on `day`, latest first */
export const useMeetingsOn = (day: Date) => {
  const meetingsStore = useStores().meetings;

  useEffect(() => {
    meetingsStore.ensureListLoadedFromStorage().catch((err) => {
      logError(err, 'Failed to hydrate meetings from local storage', {
        context: 'useMeetingsOn:ensureListLoadedFromStorage',
      });
    });
  }, [meetingsStore]);

  const meetings = meetingsStore.meetingsByLastActivity.filter((m) => {
    const ts = m.lastTranscribedAt ?? m.createdAt;
    if (!ts) return false;
    return isSameDay(new Date(ts), day);
  });
  return { meetings, isLoaded: meetingsStore.isLoaded };
};

/** The chats, newest first, last written in on `day`, or on any day when it is left out */
export const useChatsOn = (day?: Date) => {
  const threadsStore = useStores().threads;
  const chats = threadsStore.visibleChats.filter(
    (t) =>
      t.id !== NEW_CHAT_ID &&
      (day === undefined || isSameDay(t.lastMessageAt, day))
  );
  return { chats, isLoaded: threadsStore.isLoaded };
};
